import { type JSX, useId, type ComponentProps, type ReactNode } from 'react'

import { Label } from '../components/ui/label.tsx'
import { cn } from '../lib/cn.ts'

/** One choice in a `Select`. */
export interface SelectOption {
  value: string
  label: string
}

export type SelectProps = Omit<ComponentProps<'select'>, 'children'> & {
  'data-testid': string
  label: ReactNode
  options: readonly SelectOption[]
  /** Adds an empty first choice with this text. Absent means the list is the whole choice. */
  placeholder?: string
  /** A message means invalid; it also sets aria-invalid. */
  error?: string
  hint?: ReactNode
}

/**
 * Select — a labelled choice from a fixed list.
 *
 * A native `select`, not a Radix combobox. The reasons live in
 * `docs/decisions/0006-the-console-select-is-native.md`. Shaped like `Input`
 * on purpose — same id handling, same single message slot where the error
 * wins, same required `data-testid`. Two controls that look alike on the page
 * should not be two different props contracts in the source.
 *
 * @param props - Select props.
 * @param props.label - Field label, rendered above the control.
 * @param props.options - The choices, in the order they are shown.
 * @param props.placeholder - Text for an empty leading choice.
 * @param props.error - Error message; sets aria-invalid.
 * @param props.hint - Shown when there is no error.
 * @param props.id - Overrides the generated id.
 * @returns The labelled select.
 */
export function Select({
  label,
  options,
  placeholder,
  error,
  hint,
  id,
  className,
  'data-testid': testId,
  ...props
}: SelectProps): JSX.Element {
  const generated = useId()
  const fieldId = id ?? generated
  const messageId = `${fieldId}-message`
  const message = error ?? hint

  return (
    <div className="grid gap-2">
      <Label htmlFor={fieldId}>{label}</Label>
      <select
        id={fieldId}
        data-testid={testId}
        aria-invalid={error ? true : undefined}
        aria-describedby={message ? messageId : undefined}
        className={cn(
          'border-input bg-card h-9 rounded-md border px-2 text-sm',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
          error && 'border-destructive',
          className
        )}
        {...props}
      >
        {placeholder === undefined ? null : (
          <option value="">{placeholder}</option>
        )}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {message ? (
        <p
          id={messageId}
          className={cn(
            'text-sm',
            error ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {message}
        </p>
      ) : null}
    </div>
  )
}
